import React, { useMemo } from "react";
import { View, StyleSheet, StyleProp, ViewStyle, ColorValue } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Loading from "./Loading";

export type ChildrenGroupProps = {
    style?: StyleProp<ViewStyle>;
    backgroundColor?: ColorValue;
    borderWidth?: number;
    children: React.ReactNode;
    testID?: string;
    selected?: boolean;
    loading?: boolean;
};

export const ChildrenGroup: React.FC<ChildrenGroupProps> = ({
    style,
    backgroundColor,
    children,
    testID,
    selected = false,
    loading = false,
    borderWidth = 1,
}) => {
    const filteredChildren = useMemo(
        () => React.Children.toArray(children).filter(Boolean),
        [children]
    );
    const childrenCount = React.Children.count(filteredChildren);
    const listGroupStyle = useChildrenGroupStyle(backgroundColor);
    return (
        <View
            style={[
                listGroupStyle.group,
                { borderWidth: borderWidth },
                selected ? listGroupStyle.groupSelected : null,
                style,
            ]}
            testID={testID}
        >
            {React.Children.map(filteredChildren, (child, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === childrenCount - 1;
                return (
                    child && (
                        <View
                            key={`item-${idx}`}
                            style={[
                                listGroupStyle.baseItem,
                                isFirst ? listGroupStyle.firstItem : null,
                                isLast ? [listGroupStyle.lastItem] : null,
                                !isFirst && !isLast ? listGroupStyle.middleItem : null,
                            ]}
                        >
                            {child}
                        </View>
                    )
                );
            })}
            {loading && (
                <View style={listGroupStyle.loadingContainer}>
                    <Loading />
                </View>
            )}
        </View>
    );
};

const useChildrenGroupStyle = (backgroundColor?: ColorValue) => {
    const { roundness, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                group: {
                    flexDirection: "column",
                    alignSelf: "stretch",
                    borderColor: colors.backdrop,
                    borderRadius: roundness,
                },
                groupSelected: {
                    borderColor: colors.primary,
                },
                baseItem: {
                    borderRadius: 0,
                    marginVertical: 0,
                    backgroundColor: backgroundColor || colors.card,
                    paddingVertical: roundness,
                },
                firstItem: {
                    borderTopLeftRadius: roundness,
                    borderTopRightRadius: roundness,
                    borderBottomColor: colors.backdrop,
                    borderBottomWidth: 1,
                },
                lastItem: {
                    borderBottomColor: colors.backdrop,
                    borderBottomLeftRadius: roundness,
                    borderBottomRightRadius: roundness,
                    borderBottomWidth: 1,
                },
                middleItem: {
                    borderBottomColor: colors.backdrop,
                    borderBottomWidth: 1,
                },
                loadingContainer: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "'rgba(52, 52, 52, 0.5)'",
                    borderRadius: roundness,
                },
            }),
        [roundness, colors, backgroundColor]
    );
};
export default ChildrenGroup;
